import { useState, useEffect } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  ArrowDownToLine,
  ArrowLeftRight,
  ArrowUpFromLine,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  CircleAlert,
  Hourglass,
  Layers,
  Lock,
  Plus,
  ReceiptText,
} from 'lucide-react'
import { PortalShell } from '@/components/portal/PortalShell'
import { LiveAccountModal } from '@/components/portal/LiveAccountModal'
import { DemoAccountModal } from '@/components/portal/DemoAccountModal'
import { OnboardingStepper, getStepAction } from '@/components/portal/OnboardingStepper'
import { NextStepCard } from '@/components/portal/NextStepCard'
import { AccountCard } from '@/components/portal/AccountCard'
import { ComingSoonBadge } from '@/components/portal/ComingSoonBadge'
import { useOnboardingStatus } from '@/hooks/useOnboardingStatus'
import { useClientAccounts } from '@/hooks/useClientAccounts'
import { useClientId } from '@/hooks/useClientId'
import { useAuthStore } from '@/store/auth'
import {
  getOnboardingCurrentStepIndex,
  mergeChecklistItems,
  type ChecklistStepKey,
} from '@/lib/onboarding-next-step'
import { cn } from '@/lib/utils'
import { accountTypeLabel } from '@/components/portal/account-type-label'

const CAROUSEL_SLIDE_KEYS = ['promoSlide1', 'promoSlide2'] as const

/** One line of context under the step title; existing copy where the app already explains the step. */
const STEP_REASON_KEYS: Partial<Record<ChecklistStepKey, string>> = {
  email_verified: 'auth.verifyEmailHint',
  form_perjanjian_filled: 'home.nextStep.form_perjanjian_filled.reason',
  persyaratan_accepted: 'home.nextStep.persyaratan_accepted.reason',
  live_account_created: 'home.nextStep.live_account_created.reason',
  deposit_funds: 'fundingPage.depositHint',
}

export function HomePage() {
  const { t } = useTranslation()
  const user = useAuthStore((s) => s.user)
  const { clientId, resolving } = useClientId()
  const { data, isLoading, isError: statusError, refetch: refetchStatus } = useOnboardingStatus(clientId)
  const usableName = (value?: string | null) => {
    const trimmed = value?.trim() ?? ''
    return trimmed && trimmed !== '-' ? trimmed : ''
  }
  const welcomeName = usableName(data?.display_name) || usableName(user?.name)
  const {
    data: accountList,
    isLoading: accountsLoading,
    isError: accountsError,
    refetch: refetchAccounts,
  } = useClientAccounts(clientId)
  const liveAccounts = (accountList?.accounts ?? []).filter((row) => row.kind === 'live')
  const demoAccounts = (accountList?.accounts ?? []).filter((row) => row.kind === 'demo')
  const [searchParams, setSearchParams] = useSearchParams()
  const [liveModalOpen, setLiveModalOpen] = useState(false)
  const [demoModalOpen, setDemoModalOpen] = useState(false)
  const [carouselIndex, setCarouselIndex] = useState(0)
  const [stepsOpen, setStepsOpen] = useState(false)

  const openingDone = ['APPROVED_BY_WP', 'APPROVED_BY_DIRUT', 'ACTIVE'].includes(data?.status ?? '')
  const checklist = data?.checklist ?? []
  const steps = mergeChecklistItems(checklist)
  const liveIncomplete = steps.some((item) => item.key === 'live_account_created' && !item.completed)
  const onboardingComplete = steps.every((item) => item.completed)
  const cddsNav = {
    persyaratan_accepted: data?.persyaratan_accepted,
    cdds_submitted_at: data?.cdds_submitted_at,
    awaiting_approval: data?.awaiting_approval,
  }

  // "Guided path, then cockpit": once approved and only the first deposit is left,
  // the gold Deposit action takes over from the next-step hero.
  const statusLoading = resolving || isLoading
  const remaining = steps.filter((item) => !item.completed)
  const onlyDepositLeft = remaining.length > 0 && remaining.every((item) => item.key === 'deposit_funds')
  const activeMode = !statusLoading && !statusError && (onboardingComplete || (openingDone && onlyDepositLeft))
  const showHero = !statusLoading && !statusError && !activeMode
  const currentIndex = getOnboardingCurrentStepIndex(steps, {
    awaitingApproval: Boolean(data?.awaiting_approval),
    cddsNav,
    isLoading: statusLoading,
  })
  const currentStep = currentIndex >= 0 ? steps[currentIndex] : undefined
  const currentKey = currentStep?.key as ChecklistStepKey | undefined
  const waiting = currentKey === 'perjanjian_accepted'

  useEffect(() => {
    if (searchParams.get('live') === '1' && liveIncomplete) {
      setLiveModalOpen(true)
      setSearchParams({}, { replace: true })
    }
    if (searchParams.get('demo') === '1') {
      setDemoModalOpen(true)
      setSearchParams({}, { replace: true })
    }
  }, [searchParams, setSearchParams, liveIncomplete])

  const goPrev = () =>
    setCarouselIndex((i) => (i === 0 ? CAROUSEL_SLIDE_KEYS.length - 1 : i - 1))
  const goNext = () => setCarouselIndex((i) => (i + 1) % CAROUSEL_SLIDE_KEYS.length)

  function renderStepCta() {
    if (!currentKey || !currentStep) return null
    const action = getStepAction(currentKey, currentStep.completed, clientId, () => setLiveModalOpen(true), cddsNav)
    const label = t(`home.nextStep.${currentKey}.cta`, { defaultValue: t(`checklist.${currentKey}`) })
    if (action.type === 'link') {
      return (
        <Link to={action.href} className="prod-btn prod-btn--gold prod-next-step__cta">
          {label}
        </Link>
      )
    }
    if (action.type === 'button') {
      return (
        <button type="button" className="prod-btn prod-btn--gold prod-next-step__cta" onClick={action.onClick}>
          {label}
        </button>
      )
    }
    return null
  }

  const hero = showHero ? (
    <NextStepCard
      titleId="home-next-step-title"
      className="prod-home__hero"
      tone={waiting ? 'waiting' : 'action'}
      icon={waiting ? <Hourglass className="size-5" aria-hidden="true" /> : undefined}
      currentIndex={currentIndex}
      completed={steps.map((item) => item.completed)}
      title={
        waiting
          ? t('home.awaitingTitle')
          : currentKey
            ? t(`home.nextStep.${currentKey}.title`, { defaultValue: t(`checklist.${currentKey}`) })
            : t('home.progressTitle')
      }
      body={
        waiting ? (
          <p>{t('fundingPage.waitingApprovalHelper')}</p>
        ) : currentKey && STEP_REASON_KEYS[currentKey] ? (
          <p>{t(STEP_REASON_KEYS[currentKey])}</p>
        ) : undefined
      }
      action={waiting ? undefined : renderStepCta()}
    >
      <div className="prod-next-step__more">
        <button
          type="button"
          className="prod-disclosure"
          aria-expanded={stepsOpen}
          aria-controls="home-all-steps"
          onClick={() => setStepsOpen((open) => !open)}
        >
          <ChevronDown className="prod-disclosure__icon" aria-hidden="true" />
          {t('home.showAllSteps')}
        </button>
        <div id="home-all-steps" className="prod-next-step__steps" hidden={!stepsOpen}>
          <OnboardingStepper
            items={data?.checklist ?? []}
            clientId={clientId}
            isLoading={isLoading}
            awaitingApproval={Boolean(data?.awaiting_approval)}
            cddsNav={cddsNav}
            onOpenLive={() => setLiveModalOpen(true)}
          />
        </div>
      </div>
    </NextStepCard>
  ) : statusLoading ? (
    <div className="prod-next-step prod-home__hero prod-skeleton-card" aria-busy="true">
      <span className="sr-only">{t('common.loading')}</span>
      <span className="prod-skeleton prod-skeleton--line-sm" aria-hidden="true" />
      <span className="prod-skeleton prod-skeleton--bar" aria-hidden="true" />
      <span className="prod-skeleton prod-skeleton--title" aria-hidden="true" />
      <span className="prod-skeleton prod-skeleton--button" aria-hidden="true" />
    </div>
  ) : statusError ? (
    <div className="prod-inline-error prod-home__hero" role="alert">
      <CircleAlert className="prod-inline-error__icon" aria-hidden="true" />
      <p className="prod-inline-error__text">{t('home.statusError')}</p>
      <button type="button" className="prod-btn prod-btn--outline prod-btn--compact" onClick={() => void refetchStatus()}>
        {t('common.retry')}
      </button>
    </div>
  ) : null

  const lockedTitle = !openingDone ? t('fundingPage.openingRequired') : undefined
  const depositPrimary = openingDone && !showHero

  const moneyActions = (
    <section
      className={cn('prod-money', !openingDone && 'prod-money--locked')}
      aria-labelledby="home-actions-title"
    >
      <div className="prod-money__head">
        <h2 id="home-actions-title" className="prod-section-title">
          {t('home.actionsTitle')}
        </h2>
        <Link to="/funds" className="prod-money__history">
          <ReceiptText className="size-4" aria-hidden="true" />
          {t('home.funds')}
        </Link>
      </div>
      {!openingDone ? (
        <p className="prod-money__note">
          <Lock className="size-4" aria-hidden="true" />
          {t('home.moneyLocked')}
        </p>
      ) : activeMode && !onboardingComplete ? (
        <p className="prod-money__note prod-money__note--step">{t('home.lastStepDeposit')}</p>
      ) : null}
      <div className="prod-money__grid">
        <Link
          to="/funding?tab=deposit"
          className={cn('prod-money__action', depositPrimary && 'prod-money__action--primary')}
          title={lockedTitle}
        >
          <ArrowDownToLine className="prod-money__icon" aria-hidden="true" />
          <span className="prod-money__label">{t('home.deposit')}</span>
        </Link>
        <Link to="/funding?tab=withdraw" className="prod-money__action" title={lockedTitle}>
          <ArrowUpFromLine className="prod-money__icon" aria-hidden="true" />
          <span className="prod-money__label">{t('home.withdraw')}</span>
        </Link>
        <Link to="/funding?tab=transfer" className="prod-money__action prod-money__action--soon" title={lockedTitle}>
          <ArrowLeftRight className="prod-money__icon" aria-hidden="true" />
          <span className="prod-money__label">{t('home.transfer')}</span>
          <ComingSoonBadge compact />
        </Link>
      </div>
    </section>
  )

  const demoRows =
    demoAccounts.length > 0
      ? demoAccounts.map((row) => ({ key: row.login_id, loginId: row.login_id }))
      : data?.demo_account_id
        ? [{ key: data.demo_account_id, loginId: data.demo_account_id }]
        : []

  const skeletonCard = (
    <div className="prod-acct prod-skeleton-card" aria-hidden="true">
      <span className="prod-skeleton prod-skeleton--line-sm" />
      <span className="prod-skeleton prod-skeleton--title" />
      <span className="prod-skeleton prod-skeleton--line" />
    </div>
  )

  const accounts = (
    <section className="prod-accounts" aria-labelledby="home-accounts-title" aria-busy={accountsLoading || undefined}>
      <h2 id="home-accounts-title" className="prod-section-title">
        {t('home.accountsTitle')}
      </h2>
      {accountsError ? (
        <div className="prod-inline-error" role="alert">
          <CircleAlert className="prod-inline-error__icon" aria-hidden="true" />
          <p className="prod-inline-error__text">{t('home.accountsError')}</p>
          <button
            type="button"
            className="prod-btn prod-btn--outline prod-btn--compact"
            onClick={() => void refetchAccounts()}
          >
            {t('common.retry')}
          </button>
        </div>
      ) : null}
      <div className="prod-accounts__grid">
        <div className="prod-accounts__col" role="group" aria-labelledby="home-live-title">
          <h3 id="home-live-title" className="prod-accounts__col-title">
            {t('home.liveAccount')}
          </h3>
          {accountsLoading
            ? skeletonCard
            : liveAccounts.map((row) => (
                <AccountCard
                  key={row.row_id ?? row.login_id}
                  kind="live"
                  loginId={row.login_id}
                  pending={row.login_id.startsWith('pending-')}
                  typeLabel={`${t('home.liveMt5')}${row.account_type ? ` ${accountTypeLabel(row.account_type)}` : ''}`}
                  status={row.status}
                  rate={row.rate}
                  leverage={row.leverage}
                />
              ))}
          <button type="button" className="prod-acct-tile" onClick={() => setLiveModalOpen(true)}>
            <span className="prod-acct-tile__title">
              <Plus className="size-4" aria-hidden="true" />
              {t('home.openLive')}
            </span>
            <span className="prod-acct-tile__hint">{t('home.openLiveHint')}</span>
          </button>
        </div>

        <div className="prod-accounts__col" role="group" aria-labelledby="home-demo-title">
          <h3 id="home-demo-title" className="prod-accounts__col-title">
            {t('home.demoAccount')}
          </h3>
          {accountsLoading
            ? skeletonCard
            : demoRows.map((row) => (
                <AccountCard key={row.key} kind="demo" loginId={row.loginId} typeLabel={t('home.demoMt5')} />
              ))}
          <button type="button" className="prod-acct-tile" onClick={() => setDemoModalOpen(true)}>
            <span className="prod-acct-tile__title">
              <Plus className="size-4" aria-hidden="true" />
              {t('home.openDemo')}
            </span>
            <span className="prod-acct-tile__hint">{t('home.openDemoHint')}</span>
          </button>
        </div>

        <div className="prod-accounts__col" role="group" aria-labelledby="home-multi-title">
          <h3 id="home-multi-title" className="prod-accounts__col-title">
            {t('home.multilateralTitle')}
          </h3>
          {/* TODO: enable once multilateral accounts launch; the button stays disabled until then. */}
          <div className="prod-acct-tile prod-acct-tile--soon">
            <span className="prod-acct-tile__head">
              <Layers className="size-5" aria-hidden="true" />
              <ComingSoonBadge />
            </span>
            <span className="prod-acct-tile__hint">{t('home.multilateralHint')}</span>
            <button
              type="button"
              className="prod-btn prod-btn--outline prod-btn--disabled prod-btn--icon-gap prod-acct-tile__btn"
              disabled
              aria-disabled="true"
            >
              <Plus className="size-4" aria-hidden="true" />
              {t('home.multilateral')}
            </button>
          </div>
        </div>
      </div>
    </section>
  )

  const promotions = (
    <section className="prod-promo" aria-labelledby="home-promo-title">
      <h2 id="home-promo-title" className="prod-section-title prod-section-title--quiet">
        {t('home.promotionsTitle')}
      </h2>
      <div className="prod-carousel">
        <div className="prod-carousel__viewport">
          <div
            className="prod-carousel__track"
            style={{
              width: `${CAROUSEL_SLIDE_KEYS.length * 100}%`,
              transform: `translateX(-${(carouselIndex * 100) / CAROUSEL_SLIDE_KEYS.length}%)`,
            }}
          >
            {CAROUSEL_SLIDE_KEYS.map((key, i) => (
              <div key={key} className="prod-carousel__slide" aria-hidden={i !== carouselIndex}>
                <div>
                  <p className="prod-carousel__title">{t(`home.${key}Title`)}</p>
                  <p className="prod-carousel__sub">{t(`home.${key}Subtitle`)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <button
          type="button"
          className="prod-carousel__nav prod-carousel__nav--prev"
          aria-label={t('common.previous')}
          onClick={goPrev}
        >
          <ChevronLeft className="size-4" aria-hidden="true" />
        </button>
        <button
          type="button"
          className="prod-carousel__nav prod-carousel__nav--next"
          aria-label={t('common.next')}
          onClick={goNext}
        >
          <ChevronRight className="size-4" aria-hidden="true" />
        </button>
        <div className="prod-carousel__dots" role="tablist" aria-label={t('home.promotionsTitle')}>
          {CAROUSEL_SLIDE_KEYS.map((key, i) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={i === carouselIndex}
              aria-label={t(`home.${key}Title`)}
              className={`prod-carousel__dot ${i === carouselIndex ? 'prod-carousel__dot--active' : ''}`}
              onClick={() => setCarouselIndex(i)}
            />
          ))}
        </div>
      </div>
    </section>
  )

  return (
    <PortalShell>
      <div className={cn('prod-home', activeMode ? 'prod-home--active' : 'prod-home--onboarding')}>
        <header className="prod-home__greeting">
          <h1 className="prod-dashboard__hero-title">
            {welcomeName ? t('home.welcomeBack', { name: welcomeName }) : t('home.title')}
          </h1>
          <p className="prod-dashboard__hero-sub">{t('home.dashboardSubtitle')}</p>
        </header>

        {activeMode ? null : hero}
        {moneyActions}
        {accounts}
        {promotions}
      </div>

      <LiveAccountModal open={liveModalOpen} onClose={() => setLiveModalOpen(false)} />
      <DemoAccountModal open={demoModalOpen} onClose={() => setDemoModalOpen(false)} />
    </PortalShell>
  )
}
